import { useElementBounding, useScroll } from '@vueuse/core'
import { debounce, isBoolean } from 'lodash'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { ROUTE_NAMES } from '@/routes/route-helpers'
import { readFromStorageByKey, updateStorageByKey } from '@/utils/persist'

export const NAV_ITEM_PLACEMENTS = {
  TOP: 'top',
  BOTTOM: 'bottom'
}

export const APP_MENU_NAV_LAYOUTS = {
  VERTICAL: 'vertical',
  HORIZONTAL: 'horizontal'
}

export const APP_MENU_LOGO_PLACEMENTS = {
  MAIN_MENU: 'main-menu',
  ADDITIONAL_MENU: 'additional-menu'
}
export const BOTTOM_NAV_NOTIFICATIONS_PORTAL_NAME = 'app-menu-bottom-nav-notifications'

export const useAppMenuHelpers = () => {
  const menuReference = ref(null)
  const { arrivedState, measure } = useScroll(menuReference)
  const { height } = useElementBounding(menuReference)

  const debouncedMeasure = debounce(() => measure(), 500)

  watch(
    height,
    () => {
      debouncedMeasure()
    },
    { immediate: true }
  )

  return {
    menuReference,
    arrivedState
  }
}

const normalizeMenuItemName = (menuItemName = 'unknown') => {
  return menuItemName.toLowerCase().trim()
}

export const getMenuItemLabelSlotName = (menuItemName = 'unknown') => {
  return `item-label(${normalizeMenuItemName(menuItemName)})`
}

export const getMenuItemAppendSlotName = (menuItemName = 'unknown') => {
  return `item-append(${normalizeMenuItemName(menuItemName)})`
}

// The menu state was renamed from "pinned" to "expanded", but the stored value keeps the same
// meaning, so the legacy storage key is kept on purpose: renaming it would reset the saved menu
// state for every existing user.
const MENU_PINNED_STATUS_LS_KEY = 'MENU_PINNED_STATUS'

export const useAppMenu = () => {
  const store = useStore()

  const route = useRoute()

  const isHomeRoute = computed(() => {
    return route.name === ROUTE_NAMES.HOME_PAGE
  })

  const isMenuExpanded = computed(() => {
    if (isHomeRoute.value) return store.state.system.appMenu.expandedOnHomepage

    return store.state.system.appMenu.expanded
  })
  const isMenuCollapsed = computed(() => !isMenuExpanded.value)

  const toggleMenu = async () => {
    if (isHomeRoute.value) {
      await store.dispatch('system/toggleMenuExpandedStatusForHomepage')
      return
    }

    await store.dispatch('system/toggleMenuExpandedStatus', !isMenuExpanded.value)

    updateStorageByKey(MENU_PINNED_STATUS_LS_KEY, isMenuExpanded.value)
  }

  const restoreMenuState = () => {
    const storedMenuPinnedStatus = readFromStorageByKey(MENU_PINNED_STATUS_LS_KEY, null, {
      ignoreTtl: true
    })

    if (isBoolean(storedMenuPinnedStatus)) {
      store.dispatch('system/toggleMenuExpandedStatus', storedMenuPinnedStatus)
    }
  }

  return {
    isMenuCollapsed,
    isMenuExpanded,
    toggleMenu,
    restoreMenuState
  }
}
